import React from "react";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import {
  Menu, PanelLeftClose, PanelLeft, Moon, Sun, Bell, LogOut, ChevronDown, Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const ROLE_LABELS = {
  ceo: "CEO (Super Admin)",
  dept_head: "Department Head",
  center_manager: "Center Manager",
  viewer: "Viewer",
};

const DATE_RANGES = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "quarter", label: "This Quarter" },
];

export default function Header({ collapsed, setCollapsed, onOpenMobile }) {
  const { theme, setTheme } = useTheme();
  const { user, logout, viewRole, setViewRole, effectiveRole } = useAuth();
  const { centers, centerFilter, setCenterFilter, dateRange, setDateRange } = useApp();
  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header className="sticky top-0 z-20 h-16 bg-card/80 backdrop-blur-xl border-b border-border flex items-center gap-2 sm:gap-3 px-3 sm:px-6">
      <button className="lg:hidden text-muted-foreground" onClick={onOpenMobile} data-testid="open-mobile-sidebar">
        <Menu className="h-5 w-5" />
      </button>
      <button
        className="hidden lg:flex text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setCollapsed((c) => !c)}
        data-testid="toggle-sidebar"
      >
        {collapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
      </button>

      <div className="hidden md:block">
        <Select value={centerFilter} onValueChange={setCenterFilter}>
          <SelectTrigger className="w-[170px] h-9" data-testid="center-filter-select">
            <SelectValue placeholder="Center" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="center-opt-all">All Centers</SelectItem>
            {centers.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`center-opt-${c.code}`}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="hidden sm:block">
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-[140px] h-9" data-testid="date-range-picker">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DATE_RANGES.map((r) => (
              <SelectItem key={r.value} value={r.value} data-testid={`date-opt-${r.value}`}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        {user?.role === "ceo" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-1.5 hidden sm:flex" data-testid="role-switcher-dropdown">
                <Eye className="h-4 w-4" />
                <span className="text-xs max-w-[90px] truncate">View: {ROLE_LABELS[effectiveRole]}</span>
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Preview interface as</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup value={viewRole || "ceo"} onValueChange={setViewRole}>
                {Object.entries(ROLE_LABELS).map(([val, label]) => (
                  <DropdownMenuRadioItem key={val} value={val} data-testid={`view-as-${val}`}>{label}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <Button variant="ghost" size="icon" className="h-9 w-9 relative" data-testid="notifications-bell">
          <Bell className="h-[18px] w-[18px]" />
          <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-rose-500" />
        </Button>

        <Button
          variant="ghost" size="icon" className="h-9 w-9"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          data-testid="theme-toggle-button"
        >
          <Sun className="h-[18px] w-[18px] dark:hidden" />
          <Moon className="h-[18px] w-[18px] hidden dark:block" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 pl-1 sm:pl-2" data-testid="user-menu-trigger">
              <Avatar className="h-9 w-9 border border-border">
                <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs">{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden lg:block text-left leading-tight">
                <p className="text-sm font-semibold">{user?.name}</p>
                <p className="text-[11px] text-muted-foreground">{ROLE_LABELS[user?.role]}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="flex flex-col gap-1">
              <span>{user?.name}</span>
              <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
              <Badge variant="secondary" className="w-fit mt-1 text-[10px]">{ROLE_LABELS[user?.role]}</Badge>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-rose-600 focus:text-rose-600">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
